// Libs
import classNames from 'classnames/bind';
import { useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { Controller, FormProvider, useForm, UseFormReturn } from 'react-hook-form';
import { Avatar, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack } from '@mui/material';
// Components, Layouts, Pages
import { BaseInput, BaseLabel, Loading } from '~/components/common';
import { ChecklistItemForm } from '~/components/module/checklist';
// Others
import { useAppDispatch } from '~/utils/redux/hooks';
import { IChecklistForm } from '~/utils/interface/checklist';
import { createChecklist } from '~/thunks/checklist/checklistThunk';
import { checklistActions } from '~/thunks/checklist/checklistSlice';
import { ChecklistTypeEnum } from '~/utils/enum/common';
import { removeEmptyObjects } from '~/utils/helper/common';
import { defaultValuesForm, schema } from './helper';
// Styles, images, icons
import styles from './FormChecklist.module.scss';
import { icons } from '~/assets';

type Props = {
  onClose: () => void;
};

const cx = classNames.bind(styles);

const FormChecklist = (props: Props) => {
  //#region Destructuring Props
  const { onClose } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/checklist', 'common/base']);
  const dispatch = useAppDispatch();
  const {
    control,
    setValue,
    handleSubmit,
    setError,
    getValues,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<IChecklistForm>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleFormChecklist = (data: IChecklistForm) => {
    setIsLoading(true);

    const convertData = removeEmptyObjects({
      ...data,
      type: ChecklistTypeEnum.DEFAULT,
      isTemplate: true,
      checklistItems: data.checklistItems?.map(({ id, ...item }) => item),
    }) as IChecklistForm;

    dispatch(createChecklist(convertData))
      .unwrap()
      .then((res) => {
        handleCloseModal();
        dispatch(checklistActions.setRefreshChecklistList(true));
      })
      .catch((err) => {})
      .finally(() => setIsLoading(false));
  };

  const handleCloseModal = () => {
    onClose && onClose();
    reset();
  };
  //#endregion Handle Function

  return (
    <Dialog
      id='checklistFormModalComponent'
      aria-labelledby='checklistFormModal'
      open={true}
      onClose={handleCloseModal}
      fullWidth
      maxWidth={'md'}
    >
      <DialogTitle id='checklistFormModal' className={cx('header')}>
        <Stack direction={'column'} spacing={'10px'}>
          <Stack direction={'row'} spacing={10}>
            <BaseLabel label={t('checklist_form_title_add_checklist')} styleType='bigTitle' />
          </Stack>
        </Stack>

        <IconButton onClick={handleCloseModal} className={cx('buttonClose')}>
          <Avatar
            src={icons.commonIconCancel}
            className={cx('iconClose')}
            alt={t('common_alt_img', { ns: 'common/base' })}
          />
        </IconButton>
      </DialogTitle>

      <FormProvider
        {...({
          control,
          watch,
          reset,
          setError,
          setValue,
          getValues,
          formState: { errors, isDirty },
        } as UseFormReturn<IChecklistForm>)}
      >
        <DialogContent className={cx('content')} dividers>
          <Stack flex={1} gap={6}>
            <Stack width={'30%'}>
              <Controller
                control={control}
                name={'title'}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseInput
                      height={36}
                      label={t('checklist_form_title')}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors?.title?.message}
                      required
                    />
                  );
                }}
              />
            </Stack>

            <Stack flex={1}>
              <ChecklistItemForm />
            </Stack>
          </Stack>
        </DialogContent>
      </FormProvider>

      <DialogActions className={cx('groupButton')}>
        <Button variant='outlined' className={cx('buttonCancel')} onClick={handleCloseModal}>
          {t('common_action_cancel', { ns: 'common/base' })}
        </Button>

        <Button variant='contained' className={cx('buttonSave')} onClick={handleSubmit(handleFormChecklist)}>
          {t('common_action_save', { ns: 'common/base' })}
        </Button>
      </DialogActions>

      {isLoading && <Loading />}
    </Dialog>
  );
};

export default FormChecklist;
